<template>
  <modal title="Leaderboard points breakdown" @close="$emit('close')" :show-footer="true">
    <div v-for="(entry, i) in breakdown" :key="i" class="breakdown-entry">
      <div class="breakdown-entry__header">
        <div class="breakdown-entry__meta">
          <span class="breakdown-entry__label">Leaderboard</span>
          <span class="breakdown-entry__slug">{{ entry.slug }}</span>
        </div>
        <div v-if="entry.ranking_system" class="breakdown-entry__meta">
          <span class="breakdown-entry__label">Scoring</span>
          <span class="breakdown-entry__system">{{ entry.ranking_system }}</span>
        </div>
      </div>
      <table class="table breakdown-table">
        <thead>
          <tr>
            <th>Rule</th>
            <th class="text-right">Points</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(rule, j) in entry.rules" :key="j">
            <td>
              <span class="breakdown-rule__name">{{ humaniseClass(rule.class) }}</span>
              <span class="label" :class="`bg-${ruleType(rule.class)}`">{{ ruleType(rule.class) }}</span>
            </td>
            <td class="text-right breakdown-rule__points">{{ rule.points }}</td>
            <td>
              <span v-for="key in extraKeys(rule)" :key="key" class="breakdown-rule__meta">
                <span class="text-muted">{{ humaniseKey(key) }}:</span> {{ rule[key] }}
              </span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr v-if="roundUp(entry) > 0" class="breakdown-table__roundup">
            <td>Round up</td>
            <td class="text-right">{{ roundUp(entry) }}</td>
            <td></td>
          </tr>
          <tr class="breakdown-table__total">
            <td>Total points</td>
            <td class="text-right">{{ entry.score }}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
    <p v-if="breakdown.length === 0" class="text-muted">No breakdown available.</p>
  </modal>
</template>

<script>
import ModalJS from '@/components/utils/Modal';

export default {
  name: 'leaderboard-breakdown-modal',
  components: {
    ModalJS, // eslint-disable-line
    modal: ModalJS,
  },
  props: {
    breakdown: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    extraKeys(rule) {
      return Object.keys(rule).filter((key) => key !== 'class' && key !== 'points');
    },
    // The ranking system ceils the raw sum of rule contributions to the stored score,
    // so surface that rounding as an explicit line rather than an unexplained gap.
    roundUp(entry) {
      const rawTotal = (entry.rules || []).reduce((total, rule) => total + (rule.points || 0), 0);
      return Math.round((entry.score - rawTotal) * 100) / 100;
    },
    ruleType(name) {
      if (!name) return 'base';
      if (name.endsWith('Multiplier')) return 'multiplier';
      if (name.endsWith('Penalty')) return 'penalty';
      if (name.endsWith('Bonus')) return 'bonus';
      return 'base';
    },
    humaniseClass(name) {
      if (!name) return '';
      return name
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase());
    },
    humaniseKey(key) {
      if (!key) return '';
      return key
        .replace(/_/g, ' ')
        .replace(/^./, (c) => c.toUpperCase());
    },
  },
};
</script>

<style lang="scss" scoped>
.breakdown-entry {
  margin-bottom: 20px;

  &__header {
    display: flex;
    gap: 32px;
    margin-bottom: 12px;
    padding: 0 8px;
  }

  &__meta {
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #999;
  }

  &__slug {
    font-weight: bold;
    color: #333;
  }

  &__system {
    color: #333;
  }
}

.breakdown-table {
  margin-bottom: 0;
  font-size: 13px;

  thead th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #999;
    border-bottom: 2px solid #f4f4f4;
    padding: 4px 8px;
  }

  tbody td {
    padding: 6px 8px;
    vertical-align: middle;
    border-top: 1px solid #f4f4f4;
  }

  tfoot td {
    padding: 6px 8px;
  }

  &__roundup td {
    color: #888;
    font-style: italic;
    border-top: 1px solid #f4f4f4;
  }

  &__total td {
    font-weight: bold;
    color: #333;
    border-top: 2px solid #ddd;
  }
}

.breakdown-rule__name {
  font-weight: bold;
}

.breakdown-rule__points {
  font-weight: bold;
  white-space: nowrap;
}

.breakdown-rule__meta {
  display: block;
  font-size: 12px;
}

// Small status labels for the rule type (base / bonus / multiplier / penalty).
.breakdown-table .label {
  display: inline-block;
  margin-left: 6px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &.bg-multiplier { background-color: #605ca8; color: #fff; }
  &.bg-penalty { background-color: #dd4b39; color: #fff; }
  &.bg-bonus { background-color: #00a65a; color: #fff; }
  &.bg-base { background-color: #6c757d; color: #fff; }
}
</style>
